import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { mapApiErrorToUiModel } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { socialNotificationApi } from '../../api/socialNotificationApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import SafeImage from '../../components/SafeImage';
import { Button, EmptyState, ErrorState, LoadingState } from '../../components/ui';
import {
  appendUniqueNotifications,
  markAllNotificationsRead,
  mergeSocialNotification,
  normalizeSocialNotificationPage,
  normalizeSocialNotificationUnreadCount,
} from './socialNotificationModel';
import { notifySocialNotificationUnreadChanged } from './socialNotificationUnreadBus';
import NotificationCenterTabs from '../notification-center/NotificationCenterTabs';
import './social-notification.css';

const PAGE_LIMIT = 20;

function ActorAvatar({ actor }) {
  const initial = (actor.label || '?').slice(0, 1).toUpperCase();
  return (
    <SafeImage
      className="social-notification__avatar"
      src={actor.avatarUrl}
      alt=""
      fallback={(
        <span className="social-notification__avatar social-notification__avatar--fallback" aria-hidden="true">
          {initial}
        </span>
      )}
    />
  );
}

function NotificationRow({
  notification,
  markingRead,
  onMarkRead,
  onActivate,
  t,
}) {
  const unreadLabel = notification.unread
    ? t('travel.socialNotification.unread')
    : t('travel.common.read');
  const bodyId = `sn-body-${notification.notificationId}`;
  const content = (
    <>
      <ActorAvatar actor={notification.actor} />
      <div className="social-notification__content">
        <p className="social-notification__body" id={bodyId}>{notification.body}</p>
        {notification.createdAt ? (
          <p className="social-notification__timestamp">
            <time dateTime={notification.createdAt}>{notification.createdAtLabel}</time>
          </p>
        ) : null}
      </div>
      {notification.unread ? (
        <span className="social-notification__unread-dot" aria-hidden="true" />
      ) : null}
      <span className="social-notification__sr-only">{unreadLabel}</span>
    </>
  );

  return (
    <li>
      <article
        className={`social-notification__item ${notification.unread ? 'is-unread' : 'is-read'}`}
        aria-labelledby={bodyId}
      >
        {notification.href ? (
          <Link
            className="social-notification__main"
            to={notification.href}
            onClick={(event) => {
              event.preventDefault();
              onActivate(notification);
            }}
          >
            {content}
          </Link>
        ) : (
          <div className="social-notification__main">
            {content}
          </div>
        )}
        <div className="social-notification__actions">
          {notification.unread ? (
            <Button
              type="button"
              variant="secondary"
              loading={markingRead}
              onClick={() => onMarkRead(notification)}
            >
              {t('travel.socialNotification.markRead')}
            </Button>
          ) : null}
        </div>
      </article>
    </li>
  );
}

export default function NotificationsPage() {
  const { t } = useTravelTranslation();
  const navigate = useNavigate();
  const generation = useRef(0);
  const mutatingRef = useRef('');
  const loadingMoreRef = useRef(false);

  const [phase, setPhase] = useState('loading');
  const [errorModel, setErrorModel] = useState(null);
  const [actionError, setActionError] = useState('');
  const [actionStatus, setActionStatus] = useState('');
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingReadId, setMarkingReadId] = useState('');
  const [markingAll, setMarkingAll] = useState(false);
  const [badge, setBadge] = useState(null);

  const refreshBadge = useCallback(async () => {
    try {
      const countPayload = await socialNotificationApi.getUnreadCount();
      setBadge(normalizeSocialNotificationUnreadCount(countPayload));
      notifySocialNotificationUnreadChanged();
    } catch {
      setBadge(null);
      notifySocialNotificationUnreadChanged();
    }
  }, []);

  const load = useCallback(async () => {
    const requestId = ++generation.current;
    setPhase('loading');
    setErrorModel(null);
    setActionError('');
    try {
      const [listPayload, countPayload] = await Promise.all([
        socialNotificationApi.list({ limit: PAGE_LIMIT }),
        socialNotificationApi.getUnreadCount(),
      ]);
      if (requestId !== generation.current) return;
      const page = normalizeSocialNotificationPage(listPayload, t);
      setItems(page.items);
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
      setBadge(normalizeSocialNotificationUnreadCount(countPayload));
      notifySocialNotificationUnreadChanged();
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      // 保留既有 domain i18n 文案；mapper 只負責 retryable／class
      setErrorModel(mapApiErrorToUiModel(cause, { t }));
      setPhase('error');
    }
  }, [t]);

  useEffect(() => {
    load();
    return () => { generation.current += 1; };
  }, [load]);

  async function loadMore() {
    if (loadingMoreRef.current || loadingMore || !hasMore || !nextCursor) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setActionError('');
    try {
      const payload = await socialNotificationApi.list({
        limit: PAGE_LIMIT,
        cursorCreateTime: nextCursor.cursorCreateTime,
        cursorNotificationId: nextCursor.cursorNotificationId,
      });
      const page = normalizeSocialNotificationPage(payload, t);
      setItems((current) => appendUniqueNotifications(current, page.items));
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch {
      setActionError(t('travel.socialNotification.error'));
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }

  async function handleMarkRead(notification) {
    if (mutatingRef.current) return;
    if (!notification.unread) return;
    const notificationId = notification.notificationId;
    const previous = {
      unread: notification.unread,
      readAt: notification.readAt,
    };
    mutatingRef.current = notificationId;
    setMarkingReadId(notificationId);
    setActionError('');
    setActionStatus('');
    setItems((current) => mergeSocialNotification(current, notificationId, {
      unread: false,
      readAt: notification.readAt || new Date().toISOString(),
    }));
    try {
      await socialNotificationApi.markRead(notificationId);
      setActionStatus(t('travel.socialNotification.markRead'));
    } catch {
      setItems((current) => mergeSocialNotification(current, notificationId, previous));
      setActionError(t('travel.socialNotification.actionFailed'));
    } finally {
      mutatingRef.current = '';
      setMarkingReadId('');
      await refreshBadge();
    }
  }

  async function handleActivate(notification) {
    if (notification.unread) {
      // Mark-read failure must not block navigation; rollback optimistic row while mounted.
      const notificationId = notification.notificationId;
      const previous = {
        unread: notification.unread,
        readAt: notification.readAt,
      };
      setItems((current) => mergeSocialNotification(current, notificationId, {
        unread: false,
        readAt: notification.readAt || new Date().toISOString(),
      }));
      try {
        await socialNotificationApi.markRead(notificationId);
      } catch {
        setItems((current) => mergeSocialNotification(current, notificationId, previous));
        setActionError(t('travel.socialNotification.actionFailed'));
      }
      await refreshBadge();
    }
    if (notification.href) {
      navigate(notification.href);
    }
  }

  async function handleMarkAll() {
    if (markingAll || mutatingRef.current) return;
    const hasUnread = items.some((item) => item.unread) || (badge && badge.mode !== 'hidden');
    if (!hasUnread) return;
    mutatingRef.current = 'all';
    setMarkingAll(true);
    setActionError('');
    setActionStatus('');
    try {
      await socialNotificationApi.markAllRead();
      setItems((current) => markAllNotificationsRead(current));
      setActionStatus(t('travel.socialNotification.markAllDone'));
      await refreshBadge();
    } catch {
      setActionError(t('travel.socialNotification.actionFailed'));
      await refreshBadge();
    } finally {
      mutatingRef.current = '';
      setMarkingAll(false);
    }
  }

  if (phase === 'loading') {
    return (
      <main className="social-notification__state" data-testid="notifications-page-loading">
        <NotificationCenterTabs active="social" />
        <LoadingState label={t('travel.common.loading')} />
      </main>
    );
  }

  if (phase === 'error') {
    return (
      <main className="social-notification__state social-notification__state--error" data-testid="notifications-page-error">
        <NotificationCenterTabs active="social" />
        <ErrorState
          title={t('travel.socialNotification.title')}
          description={t('travel.socialNotification.error')}
          retryable={errorModel ? errorModel.retryable : true}
          retryLabel={t('travel.common.retry')}
          onRetry={load}
        />
      </main>
    );
  }

  const visibleUnread = items.some((item) => item.unread);
  const markAllEnabled = visibleUnread || (badge != null && badge.mode !== 'hidden');
  const unreadSummary = badge && badge.mode !== 'hidden'
    ? (badge.mode === 'dot'
      ? t('travel.socialNotification.badge.hasUnread')
      : badge.mode === 'capped'
        ? formatBadgeCount(t, 99, true)
        : badge.mode === 'truncated'
          ? formatBadgeCount(t, badge.count, true)
          : formatBadgeCount(t, badge.count, false))
    : '';

  return (
    <main className="social-notification">
      <NotificationCenterTabs active="social" />
      <header className="social-notification__header">
        <div className="social-notification__heading">
          <h1 className="social-notification__title">{t('travel.socialNotification.title')}</h1>
          <p className="social-notification__lead">{t('travel.socialNotification.lead')}</p>
          {unreadSummary ? (
            <p className="social-notification__unread-chip" aria-live="polite">{unreadSummary}</p>
          ) : null}
        </div>
        <Button
          type="button"
          variant="secondary"
          className="social-notification__mark-all"
          disabled={!markAllEnabled}
          loading={markingAll}
          onClick={handleMarkAll}
        >
          {t('travel.socialNotification.markAll')}
        </Button>
      </header>

      <div className="social-notification__live" aria-live="polite">
        {actionStatus ? <p role="status">{actionStatus}</p> : null}
        {actionError ? <p role="alert">{actionError}</p> : null}
      </div>

      {items.length === 0 ? (
        <EmptyState
          className="social-notification__empty"
          title={t('travel.socialNotification.empty')}
          description={t('travel.socialNotification.emptyDescription')}
        />
      ) : (
        <ul className="social-notification__list">
          {items.map((notification) => (
            <NotificationRow
              key={notification.notificationId}
              notification={notification}
              markingRead={markingReadId === notification.notificationId}
              onMarkRead={handleMarkRead}
              onActivate={handleActivate}
              t={t}
            />
          ))}
        </ul>
      )}

      {hasMore ? (
        <div className="social-notification__load-more-wrap">
          <Button
            type="button"
            variant="secondary"
            className="social-notification__load-more"
            loading={loadingMore}
            onClick={loadMore}
          >
            {t('travel.socialNotification.loadMore')}
          </Button>
        </div>
      ) : null}
    </main>
  );
}

function formatBadgeCount(t, count, atLeast) {
  const key = atLeast
    ? 'travel.socialNotification.badge.atLeast'
    : 'travel.socialNotification.badge.count';
  return t(key).replace('{count}', String(count));
}
